import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Papa from 'papaparse';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../../../redux/actions/notification';
import { RootState } from '../../../../redux/store';

// Types & Enums
import { NotificationMessageType } from '../../../../enums/notification-types';
import type { ImpressionModelConfig } from '../manage-impression-modelling';

// Hooks
import useCSVUploadResource from '../../../../react-query/hooks/use-csv-upload-resource';

// Config
import { IMPRESSION_MODELLING_UPLOAD_CONFIG_RESOURCE } from '../../../../configurations/resources-config';

// Helpers
import { isValidCSVDate } from '../../../../helpers/validator';
import { validateFileType } from '../../../../helpers/validate-csv-file';

// Components
import FormFileUpload from '../../../../components/form-fields/form-file-upload';
import InputButton from '../../../../components/inputs/input-button';
import ProgressBar from '../../../../components/progress-bar';
import LoadingSpinner from '../../../../components/loading-spinner';
import WarningMessage from '../../../../components/warning-message';

const StyledUploadContainer = styled.div`
    background-color: ${props => props.theme.colours.white};
    padding: 20px;
    margin-top: 10px;
`;

const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    width: 100%;
`;

const StyledProgressContainer = styled.div`
    margin-top: 20px;
    padding: 20px;
    background-color: ${props => props.theme.colours.white};
`;

const StyledFieldContainer = styled.div`
    margin-bottom: 15px;
`;

const StyledLabel = styled.label`
    display: block;
    font-weight: 600;
    margin-bottom: 5px;
`;

const StyledSelect = styled.select`
    width: 100%;
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
`;

type CsvRow = {
    impression_date: string;
    impression_visitors: string;
    total_impressions: string;
};

const CSV_FILE_HEADERS = ['impression_date', 'impression_visitors', 'total_impressions'];

type LayoutImpressionDataUploadProps = {
    configs: ImpressionModelConfig[];
};

const LayoutImpressionDataUpload = ({ configs }: LayoutImpressionDataUploadProps) => {
    const dispatch = useDispatch();
    const account = useSelector((state: RootState) => state.account);

    const [selectedConfigId, setSelectedConfigId] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFileIsValid, setSelectedFileIsValid] = useState(false);
    const [fileInputErrorMessage, setFileInputErrorMessage] = useState('');
    const [formData, setFormData] = useState<FormData | null>(null);
    const [showUploadSection, setShowUploadSection] = useState(false);

    const { uploadIsLoading, uploadIsUploading, uploadComplete, uploadProgress, uploadError, returningMessage } =
        useCSVUploadResource({
            fileUploadProgressResource: IMPRESSION_MODELLING_UPLOAD_CONFIG_RESOURCE,
            formData,
        });

    useEffect(() => {
        setShowUploadSection(!(uploadComplete || uploadIsUploading));
    }, [uploadComplete, uploadIsUploading]);

    const validateHeadersCSVFile = (headers: string[]) => {
        const sortedHeaders = [...headers].sort().map(h => h.toLowerCase());
        return (
            CSV_FILE_HEADERS.length === sortedHeaders.length &&
            CSV_FILE_HEADERS.every((header, index) => header === sortedHeaders[index])
        );
    };

    const validateDataCSVFile = (data: CsvRow[]): { isValid: boolean; errorMessage: string } => {
        for (let i = 0; i < data.length; i++) {
            const row = data[i];

            if (!row.impression_date || row.impression_date.trim() === '') {
                return { isValid: false, errorMessage: 'impression_date is empty.' };
            }

            if (!isValidCSVDate(row.impression_date)) {
                return {
                    isValid: false,
                    errorMessage: 'impression_date is not a valid date (expected YYYY-MM-DD HH:mm:ss).',
                };
            }

            if (!row.total_impressions || row.total_impressions.trim() === '') {
                return { isValid: false, errorMessage: 'total_impressions is empty.' };
            }

            if (isNaN(Number(row.total_impressions))) {
                return { isValid: false, errorMessage: 'total_impressions is not a valid number.' };
            }

            if (!row.impression_visitors || row.impression_visitors.trim() === '') {
                return { isValid: false, errorMessage: 'impression_visitors is empty.' };
            }

            if (isNaN(Number(row.impression_visitors))) {
                return { isValid: false, errorMessage: 'impression_visitors is not a valid number.' };
            }
        }

        return { isValid: true, errorMessage: '' };
    };

    const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;

        const file = event.target.files[0];

        const { isValid, message } = validateFileType(file, 'csv');
        if (!isValid) {
            setSelectedFileIsValid(false);
            setSelectedFile(null);
            setFileInputErrorMessage(message);
            return;
        }

        try {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = fileEvent => {
                const csvData: string = fileEvent.target!.result as string;

                Papa.parse(csvData, {
                    skipEmptyLines: true,
                    header: true,
                    complete: result => {
                        if (!result.data || result.data.length === 0 || !result.meta.fields) {
                            setSelectedFileIsValid(false);
                            setSelectedFile(null);
                            setFileInputErrorMessage(
                                "Sorry, this file doesn't appear to have data. Please check it and try again."
                            );
                            return;
                        }

                        if (!validateHeadersCSVFile(result.meta.fields)) {
                            setSelectedFileIsValid(false);
                            setSelectedFile(null);
                            setFileInputErrorMessage(
                                "Sorry, this file doesn't appear to be in the correct format. Expected columns: impression_date, impression_visitors, total_impressions."
                            );
                            return;
                        }

                        const { isValid: dataValid, errorMessage } = validateDataCSVFile(result.data as CsvRow[]);
                        if (!dataValid) {
                            setSelectedFileIsValid(false);
                            setSelectedFile(null);
                            setFileInputErrorMessage(`File validation failed: ${errorMessage}`);
                            return;
                        }

                        setSelectedFileIsValid(true);
                        setSelectedFile(file);
                        setFileInputErrorMessage('');
                    },
                });
            };
            reader.onerror = () => {
                setSelectedFileIsValid(false);
                setSelectedFile(null);
                setFileInputErrorMessage(
                    'Sorry, there was a problem reading this file. Please check it and try again.'
                );
            };
        } catch {
            setSelectedFileIsValid(false);
            setSelectedFile(null);
            setFileInputErrorMessage('Sorry, there was a problem reading this file. Please check it and try again.');
        }
    };

    const onClearFileSelection = () => {
        setSelectedFileIsValid(false);
        setSelectedFile(null);
        setFileInputErrorMessage('');
    };

    const handleUploadClick = () => {
        if (!selectedFile || !selectedFileIsValid || !selectedConfigId) return;

        const selectedConfig = configs.find(c => c.id === Number(selectedConfigId));
        if (!selectedConfig) return;

        const data = new FormData();
        data.append('upload_type', 'impression_modelling_csv');
        data.append('account_id', account.id);
        data.append('file', selectedFile);
        data.append('referer_id', String(selectedConfig.refererId));
        data.append('source', selectedConfig.sourceName);
        setFormData(data);
        setShowUploadSection(false);

        dispatch(
            addNotification({
                copy: 'File upload has started.',
                type: NotificationMessageType.Success,
            })
        );
    };

    const handleCloseProgress = () => {
        setShowUploadSection(true);
        setSelectedFile(null);
        setSelectedFileIsValid(false);
        setFileInputErrorMessage('');
        setFormData(null);
        setSelectedConfigId('');
    };

    return (
        <div>
            <h2>Upload Impression Data</h2>
            <p>
                Upload your impression data via CSV. You can download the template{' '}
                <a href="/files/impressions_modelling.csv">here</a>.
            </p>
            {uploadIsLoading && <LoadingSpinner />}
            {!uploadIsLoading && uploadError && (
                <StyledProgressContainer>
                    <WarningMessage
                        copy={
                            returningMessage || 'The file upload was unsuccessful. Please contact support@cubed.email.'
                        }
                    />
                </StyledProgressContainer>
            )}{' '}
            {!uploadIsLoading && !showUploadSection && !uploadError && (
                <StyledProgressContainer>
                    {returningMessage && <p>{returningMessage}</p>}
                    <p>{`File upload ${uploadProgress === '100.00' ? 'complete!' : 'in progress...'}`}</p>
                    <ProgressBar value={parseFloat(uploadProgress) / 100} difference={100} label="Upload Progress" />
                    {uploadProgress === '100.00' && (
                        <StyledButtonContainer>
                            <InputButton value="CLOSE" onClick={handleCloseProgress} />
                        </StyledButtonContainer>
                    )}
                </StyledProgressContainer>
            )}
            {showUploadSection && !uploadIsLoading && !uploadError && (
                <StyledUploadContainer>
                    <StyledFieldContainer>
                        <StyledLabel>Impression Config:</StyledLabel>
                        <StyledSelect value={selectedConfigId} onChange={e => setSelectedConfigId(e.target.value)}>
                            <option value="">-- Select a configuration --</option>
                            {configs.map(config => (
                                <option key={config.id} value={config.id}>
                                    {`SOURCE: ${config.sourceName}, CHANNEL: ${config.channelName}`}
                                </option>
                            ))}
                        </StyledSelect>
                    </StyledFieldContainer>

                    <FormFileUpload
                        label="CSV File:"
                        inputKeyValue="upload-csv-file"
                        onChange={onFileInputChange}
                        onClear={onClearFileSelection}
                        isValid={selectedFileIsValid}
                        uploadedFile={selectedFile?.name}
                        errorMessage={fileInputErrorMessage}
                        requiredField={true}
                        toolTipCopy="Upload a CSV file with columns: impression_date, impression_visitors, total_impressions."
                    />

                    <StyledButtonContainer>
                        <InputButton
                            value="UPLOAD"
                            onClick={handleUploadClick}
                            disabled={
                                !(
                                    selectedFileIsValid &&
                                    selectedFile &&
                                    selectedConfigId &&
                                    !(uploadIsUploading || uploadComplete)
                                )
                            }
                            isLoading={uploadIsUploading}
                        />
                    </StyledButtonContainer>
                </StyledUploadContainer>
            )}
        </div>
    );
};

export default LayoutImpressionDataUpload;
